import { PageWrapper, Spin } from "@/components";
import HistoryLogTable from "@/components/History/HistoryLogTable";
import { extractLookups } from "@/containers/Admin/ConditionalAccess/helper";
import { CONTENT_WIDTH, GUTTER_SIZE } from "@/enums";
import { routes } from "@/helpers";
import {
  useGetConditionalAccessGroupQuery,
  useGetFormLookupsQuery,
  useGetHistoryLogsByIdQuery,
  useSaveConditionalAccessGroupMutation,
} from "@/services";
import { Button, Card, Col, Form, Input, Row, Select, Typography } from "antd";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { ConditionalAccessGroupMembers } from "./ConditionalAccessGroupMembers";

export const ConditionalAccessGroup = () => {
  const { id } = useParams();
  const intl = useIntl();

  const [lookups, setLookups] = useState({
    projectTypes: [],
    productCategories: [],
  });
  const [formData, setFormData] = useState();

  const {
    data: { metadata, name, domain } = {},
    isFetching: isFetchingConditionalAccessGroup,
    isGetConditionalAccessGroupError,
  } = useGetConditionalAccessGroupQuery(id, { skip: !id });

  const [saveConditionalAccessGroup] = useSaveConditionalAccessGroupMutation();
  const { refetch: refetchHistory } = useGetHistoryLogsByIdQuery({
    parentEntityId: id,
  });

  const { data: formLookupState, isFetching: isFetchingFormLookups } =
    useGetFormLookupsQuery();

  const isLoading = isFetchingFormLookups || isFetchingConditionalAccessGroup;

  useEffect(() => {
    if (!isGetConditionalAccessGroupError) return;

    toast.error(intl.formatMessage({ id: "general.toastSomethingWentWrong" }));
  }, [intl, isGetConditionalAccessGroupError]);

  useEffect(() => {
    if (!formLookupState) return;

    setLookups(extractLookups(formLookupState, intl));
  }, [formLookupState, intl]);

  useEffect(() => {
    if (!id || !metadata) return;

    let projectTypes = [];
    let productCategories = [];

    if (metadata) {
      const propertyName = Object.keys(metadata).find((key) =>
        key.startsWith("conditionalAccess_")
      );
      const parsedMetadata = propertyName && JSON.parse(metadata[propertyName]);

      projectTypes = parsedMetadata?.projTypes || projectTypes;
      productCategories = parsedMetadata?.prodCats || productCategories;
    }

    setFormData({
      id,
      projectTypes,
      productCategories,
    });
  }, [id, metadata]);

  const handleFormSubmit = (metadata) => {
    saveConditionalAccessGroup({ id, metadata })
      .unwrap()
      .then(() => {
        refetchHistory();
        toast.success(
          intl.formatMessage({
            id: "conditionalAccess.saved",
          })
        );
      })
      .catch(() => {
        toast.error(
          intl.formatMessage({ id: "general.toastSomethingWentWrong" })
        );
      });
  };

  if (isLoading || !formData) return <Spin loading={isLoading} />;

  return (
    <PageWrapper
      pageTitle={intl.formatMessage({ id: "conditionalAccess.title" })}
      backButtonRoute={routes.admin.conditionalAccess.list}
    >
      <Row gutter={GUTTER_SIZE.large}>
        <Col span={CONTENT_WIDTH.half}>
          <Row className="pb-2">
            <Col span={CONTENT_WIDTH.max}>
              <Card
                title={intl.formatMessage({ id: "layout.detailsCardTitle" })}
                loading={isLoading}
              >
                <Typography.Text>
                  {intl.formatMessage({ id: "layout.nameInputLabel" })}
                </Typography.Text>
                <Input className="mt-1" value={name} disabled />
              </Card>
            </Col>
          </Row>
          <Row className="pb-2">
            <Col span={CONTENT_WIDTH.max}>
              <Card
                title={intl.formatMessage({
                  id: "conditionalAccess.grantReadOnlyAccess",
                })}
                loading={isLoading}
              >
                <Form
                  onFinish={handleFormSubmit}
                  initialValues={formData}
                  layout="vertical"
                >
                  <Form.Item
                    label={intl.formatMessage({
                      id: "conditionalAccess.projectTypes",
                    })}
                    name="projectTypes"
                  >
                    <Select
                      fieldNames={{
                        label: "label",
                        value: "id",
                      }}
                      options={lookups.projectTypes}
                      allowClear
                      style={{ width: "100%" }}
                      mode="multiple"
                    />
                  </Form.Item>
                  <Form.Item
                    label={intl.formatMessage({
                      id: "general.productCategories",
                    })}
                    name="productCategories"
                    help={intl.formatMessage({
                      id: "conditionalAccess.productCategoriesHelp",
                    })}
                  >
                    <Select
                      fieldNames={{
                        label: "label",
                        value: "id",
                      }}
                      options={lookups.productCategories}
                      allowClear
                      style={{ width: "100%" }}
                      placeholder={intl.formatMessage({
                        id: "conditionalAccess.productCategoriesPlaceholder",
                      })}
                      mode="multiple"
                    />
                  </Form.Item>
                  <Form.Item className="pt-2">
                    <Button
                      data-test-id="conditional-access-group-submit-form-button"
                      htmlType="submit"
                      loading={isLoading}
                      type="primary"
                    >
                      {intl.formatMessage({ id: "general.save" })}
                    </Button>
                  </Form.Item>
                </Form>
              </Card>
            </Col>
          </Row>
        </Col>
        <Col span={CONTENT_WIDTH.half} className="pb-2 flex">
          <Card loading={isLoading} className="flex-1">
            <HistoryLogTable parentEntityId={id} />
          </Card>
        </Col>
      </Row>
      <Row>
        <Col span={CONTENT_WIDTH.max}>
          <ConditionalAccessGroupMembers id={id} domain={domain} />
        </Col>
      </Row>
    </PageWrapper>
  );
};
